import type { Faq } from 'libs/http/api/content/content.types';

import { Button } from 'ui/atoms/Button/Button';
import type { EditorialStep } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection.types';
import type { DifferenceItem } from 'ui/organisms/DifferenceSection/DifferenceSection.types';
import type { NumberedFeatureCard } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards.types';
import type { NumberedRow } from 'ui/organisms/NumberedRowsSection/NumberedRowsSection.types';

import {
  ApiIcon,
  AskIcon,
  CrmIcon,
  DashboardIcon,
  SparkIcon,
  StackIcon,
} from './sections/DeliveryChannels/DeliveryIcons';
import { VerificationRecordCard } from './sections/VerificationRecordCard/VerificationRecordCard';
import type { DeliveryGroup } from './sections/DeliveryChannels/DeliveryChannels.types';
import type { ChangeFeedRow } from './sections/PerpetualKyb/PerpetualKyb.types';
import type { RegistrySource } from './sections/RegistrySourcesCard/RegistrySourcesCard.types';
import type { ReliabilityColumn } from './sections/ReliabilityComparison/ReliabilityComparison.types';

type Translate = (key: string) => string;

const whyLink = (label: string, href: string) => (
  <Button type="text" size="sm" href={href} suffixIcon="arrow-14">
    {label}
  </Button>
);

export const getWhyPrimarySourceRows = (t: Translate): NumberedRow[] => [
  {
    index: '01',
    title: t('Every field names the registry it came from.'),
    body: whyLink('🇬🇧 Companies House', 'https://find-and-update.company-information.service.gov.uk/'),
  },
  {
    index: '02',
    title: t('Every field carries the moment it was read.'),
    body: '2026-07-17 09:41:02 UTC',
  },
  {
    index: '03',
    title: t('Every field links to the filing it was read from.'),
    body: whyLink('Full accounts, filed 30 Apr 2026', '#'),
  },
  {
    index: '04',
    title: t('Every official document is there to pull down.'),
    body: whyLink('Certificate of incorporation · PDF', '#'),
  },
  {
    index: '05',
    title: t('Every record is yours to redistribute.'),
    body: whyLink(t('Licensing terms'), '/contact-us'),
  },
];

export const getReliabilityColumns = (t: Translate): [ReliabilityColumn, ReliabilityColumn] => [
  {
    title: t('Hosted registry data'),
    sub: t('Read from our infrastructure'),
    highlighted: true,
    points: [
      {
        title: t('Every register, not just the ones with an API'),
        description: t('Including the many that publish no machine interface.'),
      },
      {
        title: t('The same answer, months later'),
        description: t('A record can be re-checked as it stood on the day.'),
      },
      {
        title: t('Linkage already resolved'),
        description: t('Ownership chains joined across borders and schemes.'),
      },
      {
        title: t('99.9% uptime'),
        description: t('A registry outage never becomes your outage.'),
      },
      {
        title: t('Any volume, in bulk or one by one'),
        description: t('No rate limit inherited from the register.'),
      },
      {
        title: t('Perpetual monitoring'),
        description: t('Re-read daily, with the change pushed to you.'),
      },
    ],
  },
  {
    title: t('Live registry lookup'),
    sub: t('Read from the register, on request'),
    points: [
      {
        title: t('Only where an API exists'),
        description: t('Where there is no interface, there is no lookup.'),
      },
      {
        title: t("Only today's state"),
        description: t('A register shows now, not what it said when you cleared the file.'),
      },
      {
        title: t('Linkage assembled by you'),
        description: t('Every hop is another query, in another scheme.'),
      },
      {
        title: t('Outages are frequent'),
        description: t('Maintenance and downtime take the check with them.'),
      },
      {
        title: t('One entity per request, rate limited'),
        description: t('Backfills and portfolio reviews run entity by entity.'),
      },
      {
        title: t('Monitoring is impractical'),
        description: t('Re-querying the whole book on a schedule is not viable.'),
      },
    ],
  },
];

export const getUseCaseCards = (t: Translate): NumberedFeatureCard[] => [
  {
    num: '01',
    tag: t('Compliance'),
    title: t('KYB & registry verification'),
    description: t('Verify businesses straight from official company registries, with source on every field.'),
  },
  {
    num: '02',
    tag: t('Risk & Ops'),
    title: t('Onboarding & counterparty checks'),
    description: t('Confirm identity, status, and address before you contract, pay, or extend credit.'),
  },
  {
    num: '03',
    tag: 'MLRO',
    title: t('AML & compliance screening'),
    description: t('Anchor AML programmes to the registry of record, with provenance you can trace.'),
  },
  {
    num: '04',
    tag: t('Procurement'),
    title: t('Third-party & supplier due diligence'),
    description: t('Screen vendors and partners across jurisdictions worldwide from a single source.'),
  },
  {
    num: '05',
    tag: t('RevOps & Data'),
    title: t('Data enrichment & CRM accuracy'),
    description: t('Append verified identifiers and keep records current, matched on open IDs you own.'),
  },
];

export const getVerifyItems = (t: Translate): DifferenceItem[] => [
  {
    number: '01',
    title: t('Company registry data'),
    description: t('Legal identity & status.'),
    media: <VerificationRecordCard layer="registry" />,
  },
  {
    number: '02',
    title: t('Directors & officers'),
    description: t('Appointments & resignations.'),
    media: <VerificationRecordCard layer="directors" />,
  },
  {
    number: '03',
    title: t('Shareholders & UBO'),
    description: t('Beneficial ownership.'),
    media: <VerificationRecordCard layer="ubo" />,
  },
  {
    number: '04',
    title: t('Financials'),
    description: t('Filed accounts & modelled figures.'),
    media: <VerificationRecordCard layer="financials" />,
  },
  {
    number: '05',
    title: t('Group structure'),
    description: t('Parent & subsidiaries.'),
    media: <VerificationRecordCard layer="group" />,
  },
];

export const getSourceSteps = (t: Translate): EditorialStep[] => [
  {
    num: '01',
    title: t('Registry of record'),
    description: t(
      'The named government registry the field was retrieved from — the same source a regulator would check.',
    ),
    iconType: 'headquarter-building',
  },
  {
    num: '02',
    title: t('Direct filing link'),
    description: t(
      'A link to the underlying filing on the registry, so any reviewer can confirm the record independently.',
    ),
    iconType: 'payg-source-documents',
  },
  {
    num: '03',
    title: t('Original document'),
    description: t('The filed document itself, retrievable exactly as the registry published it.'),
    iconType: 'file',
  },
  {
    num: '04',
    title: t('Retrieval timestamp'),
    description: t('The exact moment the record was pulled, giving your audit trail a defensible point in time.'),
    iconType: 'duration',
  },
];

// Sample change-feed rows for the (aria-hidden) PerpetualKyb specimen widget — not translated.
export const CHANGE_FEED_ROWS: ChangeFeedRow[] = [
  {
    company: 'Northgate Supplies Ltd',
    change: 'Status: Active → In liquidation',
    time: '2h ago',
    severity: 'high',
  },
  {
    company: 'Acme Trading Ltd',
    change: 'Director resigned — E. J. Whitfield',
    time: '6h ago',
    severity: 'warn',
  },
  {
    company: 'Hallerton Holdings Ltd',
    change: 'New PSC — 32% shareholding',
    time: '1d ago',
    severity: 'warn',
  },
  {
    company: 'Meridian Logistics BV',
    change: 'Registered office updated',
    time: '1d ago',
    severity: 'info',
  },
  {
    company: 'Acme Trading Ltd',
    change: 'Full accounts filed — FY2025',
    time: '2d ago',
    severity: 'info',
  },
];

export const getDeliveryGroups = (t: Translate): DeliveryGroup[] => [
  {
    category: t('Query it directly'),
    items: [
      {
        icon: <ApiIcon />,
        name: 'REST API',
        description: t('Verify inside your onboarding flow; the full attributed record as JSON.'),
        cta: { kind: 'link', label: t('Read the API docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' },
      },
      {
        icon: <DashboardIcon />,
        name: t('Dashboard'),
        description: t('Search, verify, and export from the browser — no integration required.'),
        cta: { kind: 'link', label: t('Start a free trial'), href: '/free-trial' },
      },
    ],
  },
  {
    category: t('Ask in plain language'),
    items: [
      {
        icon: <SparkIcon />,
        name: 'Claude & ChatGPT',
        description: t('Run KYB checks in plain language inside Claude or ChatGPT, with sources attached.'),
        cta: {
          kind: 'buttons',
          buttons: [
            { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
            { label: t('Add to ChatGPT'), href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
          ],
        },
      },
      {
        icon: <AskIcon />,
        name: 'Ask Regis',
        description: t('Put questions to Regis, our AI analyst, and get verified company answers with sources.'),
        cta: { kind: 'link', label: 'Ask Regis', href: '/regis' },
      },
    ],
  },
  {
    category: t('Integrate at scale'),
    items: [
      {
        icon: <StackIcon />,
        name: t('Bulk data feeds'),
        description: t('Scheduled files via SFTP or straight into your warehouse.'),
        cta: { kind: 'link', label: t('Request a data feed'), href: '/book-a-demo' },
      },
      {
        icon: <CrmIcon />,
        name: t('CRM integrations'),
        description: t('Enrich Salesforce, HubSpot, and Dynamics 365.'),
        cta: {
          kind: 'buttons',
          buttons: [
            { label: 'Salesforce', href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4' },
            { label: 'HubSpot', href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment' },
          ],
        },
      },
    ],
  },
];

// Government registries shown in the hero specimen ticker (proper nouns — not translated).
export const REGISTRY_SOURCES: RegistrySource[] = [
  { flag: '🇬🇧', registry: 'Companies House', country: 'United Kingdom' },
  { flag: '🇺🇸', registry: 'SEC EDGAR', country: 'United States' },
  { flag: '🇩🇪', registry: 'Handelsregister', country: 'Germany' },
  { flag: '🇫🇷', registry: 'Registre du Commerce et des Sociétés', country: 'France' },
  { flag: '🇪🇸', registry: 'Registro Mercantil', country: 'Spain' },
  { flag: '🇳🇱', registry: 'Kamer van Koophandel', country: 'Netherlands' },
  { flag: '🇮🇹', registry: 'Registro delle Imprese', country: 'Italy' },
  { flag: '🇸🇬', registry: 'ACRA', country: 'Singapore' },
  { flag: '🇨🇦', registry: 'Corporations Canada', country: 'Canada' },
  { flag: '🇦🇺', registry: 'ASIC', country: 'Australia' },
  { flag: '🇸🇪', registry: 'Bolagsverket', country: 'Sweden' },
  { flag: '🇳🇴', registry: 'Brønnøysundregistrene', country: 'Norway' },
  { flag: '🇮🇳', registry: 'Ministry of Corporate Affairs', country: 'India' },
  { flag: '🇭🇰', registry: 'Companies Registry', country: 'Hong Kong' },
];

export const getFaqItems = (t: Translate): Faq[] => [
  {
    id: 'source',
    default_opened: true,
    question: t('Where does Global Database source its company data?'),
    answer: t(
      "Directly from the government registry of record in each jurisdiction — over 400 official registries across 200+ countries. It's primary-source data, not a repackaged third-party aggregate.",
    ),
  },
  {
    id: 'current',
    question: t('How current is the data?'),
    answer: t(
      'Registry records are synced daily, so a check reflects the latest filed position. Each field also carries the exact timestamp it was retrieved.',
    ),
  },
  {
    id: 'coverage',
    question: t('Which countries and registries are covered?'),
    answer: t(
      '200+ countries and 400+ registries, spanning 600M+ companies. We can confirm coverage for the specific jurisdictions you operate in.',
    ),
  },
  {
    id: 'requirements',
    question: t('What do I need to run a check?'),
    answer: t(
      'A company name, registration number, or VAT. Fuzzy matching resolves ambiguous or partial names to the correct legal entity before the record is returned.',
    ),
  },
  {
    id: 'attribution',
    question: t('How does source attribution work?'),
    answer: t(
      'Every field returns with the registry it came from, a direct link to the underlying filing, and the retrieval timestamp — so any reviewer or regulator can trace a value back to source.',
    ),
  },
  {
    id: 'identifier',
    question: t('Do you use a proprietary company identifier?'),
    answer: t(
      'No lock-in. Records are keyed to open registry numbers and LEIs, so your verified data stays portable and yours to use across your own systems.',
    ),
  },
  {
    id: 'redistribute',
    question: t('Can I redistribute the data in my own product?'),
    answer: t(
      'Yes — redistribution rights are available under licence, so you can build verified registry data into your own products and screening flows.',
    ),
  },
  {
    id: 'modelled',
    question: t('How are modelled fields handled?'),
    answer: t(
      'Fields taken from a filing are shown as filed. Estimated figures such as modelled revenue or headcount are clearly labelled as modelled, and never presented as registry data.',
    ),
  },
  {
    id: 'monitoring',
    question: t('Can I keep companies monitored after onboarding?'),
    answer: t(
      'Yes. Move any verified company into Perpetual KYB and get notified when its registry record changes — status, officers, address, or ownership.',
    ),
  },
  {
    id: 'integrate',
    question: t('How do I integrate?'),
    answer: t(
      'Through the REST API, the browser dashboard, or connectors for Claude and ChatGPT. The same attributed record is available across all of them.',
    ),
  },
];
